import React, { useEffect, useState, useRef } from "react";
import { SERVER_URL } from "../lib/constants";
import { useLocation } from "react-router-dom";
import { googleLogout } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import { UserIcon } from "@heroicons/react/solid";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import logo from "../../assets/cmxlogo-removebg-preview.png";
import searchIcon from "../../assets/search_symbol.png";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/solid";
import UserService from "../../service/UserService";
import { apiFetch } from "../lib/apiFetch";

//To know how long the job item was posted
dayjs.extend(relativeTime);

const Applications = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const appId = location.state?.appId;
  const [jobList, setJobList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [cvFile, setCvFile] = useState(null);
  const [referralCode, setReferralCode] = useState("");
  const [referralInfo, setReferralInfo] = useState(null);
  const [referralError, setReferralError] = useState("");
  const [isReferralValid, setIsReferralValid] = useState(true);
  const [referralStatusMessage, setReferralStatusMessage] = useState("");
  const [showReferralPrompt, setShowReferralPrompt] = useState(false);
  const [showReferralInput, setShowReferralInput] = useState(false);

  const [user, setUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const filteredJobs = jobList.filter((job) => {
    const search = searchTerm.toLowerCase();
    return (
      job.id?.toLowerCase().includes(search) ||
      job.title?.toLowerCase().includes(search) ||
      job.department?.toLowerCase().includes(search) || // 👈 was account
      job.email?.toLowerCase().includes(search) ||
      job.email_provider?.toLowerCase().includes(search) ||
      job.firstname?.toLowerCase().includes(search) ||
      job.middlename?.toLowerCase().includes(search) ||
      job.lastname?.toLowerCase().includes(search)
    );
  });

  const dropdownRef = useRef();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [userApplications, setUserApplications] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshJobs, setRefreshJobs] = useState(0); // ⬅️ trigger re-fetch

  useEffect(() => {
    const storedEmail = localStorage.getItem("applicantEmail");
    const storedFirstname = localStorage.getItem("applicantFirstname");
    const storedLastname = localStorage.getItem("applicantLastname");
    const storedMiddlename = localStorage.getItem("applicantMiddlename");
    const storedPic = localStorage.getItem("applicantPicture");

    if (storedEmail && storedFirstname && storedLastname) {
      setUser({
        email: storedEmail,
        firstname: storedFirstname,
        middlename: storedMiddlename || "",
        lastname: storedLastname,
        picture: storedPic || "/default-avatar.png",
      });
    } else {
      console.warn("Missing required user data from localStorage");
    }
  }, []);

  useEffect(() => {
    const u = UserService.getApplicant();
    if (u?.email) {
      setUser({
        email: u.email,
        firstname: u.firstname,
        middlename: u.middlename || "",
        lastname: u.lastname,
        picture: u.picture || "/default-avatar.png",
      });
    } else {
      // extra safeguard if someone bypasses
      navigate("/OauthLogin");
    }
  }, [navigate]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getPostedAgo = (postedDate) => {
    const now = dayjs();
    const posted = dayjs(postedDate);

    const diffInYears = now.diff(posted, "year");
    const diffInMonths = now.diff(posted, "month");
    const diffInDays = now.diff(posted, "day");

    if (diffInYears >= 1)
      return `${diffInYears} yr${diffInYears > 1 ? "s" : ""}`;
    if (diffInMonths >= 1)
      return `${diffInMonths} mo${diffInMonths > 1 ? "s" : ""}`;
    return `${diffInDays} day${diffInDays > 1 ? "s" : ""}`;
  };

  const handleLogout = async () => {
    try {
      googleLogout();
    } catch {}

    await apiFetch(`${SERVER_URL}/api/auth/logout`, {
      method: "POST",
      credentials: "include",
    });

    navigate("/OauthLogin");
  };

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await apiFetch(
          `${SERVER_URL}/api/applications/active-jobs`,
        );
        const data = await res.json();

        if (data.success) {
          const formattedJobs = data.jobs.map((job) => ({
            id: job.job_code,
            title: job.position_title,
            type: "Full-time",
            department: job.department, // 👈 was account
            status: job.status,
            workSetup: job.workSetup || "", // ✅ Add this
            description: job.job_description || "No description provided.",
            requirements: job.job_requirements || "No qualifications listed.",
            postedDate: job.posted_date, // ✅ make sure this is coming from backend
          }));

          setJobList(formattedJobs);
        } else {
          console.error("Failed to load job postings:", data.error);
        }
      } catch (error) {
        console.error("Error fetching jobs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [refreshJobs]);

  useEffect(() => {
    if (!user?.email) return;

    const fetchUserApplications = async () => {
      try {
        const res = await apiFetch(
          `${SERVER_URL}/api/applications/user-applications?email=${encodeURIComponent(
            user.email,
          )}`,
        );
        const data = await res.json();

        if (res.ok) {
          setUserApplications(data.applications); // ✅ now array of objects: { job_code, status }
        } else {
          console.error("❌ Failed to fetch user applications:", data);
        }
      } catch (err) {
        console.error("🔥 Error fetching user applications:", err);
      }
    };

    fetchUserApplications();
  }, [user]);

  const MAX_PENDING_APPLICATIONS = 1;

  const pendingCount = userApplications.filter(
    (app) => app.status !== "Failed",
  ).length;

  const hasReachedLimit = pendingCount >= MAX_PENDING_APPLICATIONS;

  // Auto-validate on input with debounce
  useEffect(() => {
    if (!referralCode.trim()) {
      setIsReferralValid(null);
      setReferralStatusMessage("");
      return;
    }
    if (!user?.email) return; // wait until user is loaded

    const controller = new AbortController();
    const delay = setTimeout(async () => {
      try {
        const res = await apiFetch(
          `${SERVER_URL}/api/applications/validate-referral/${encodeURIComponent(
            referralCode,
          )}?email=${encodeURIComponent(user.email)}`,
          { signal: controller.signal },
        );
        const data = await res.json();

        if (data.valid) {
          setIsReferralValid(true);
          setReferralStatusMessage("Referral code is valid");
        } else {
          setIsReferralValid(false);
          setReferralStatusMessage(
            data.reason === "selfReferral"
              ? "You can't use your own employee ID as a referral."
              : "Referral code is invalid",
          );
        }
      } catch (e) {
        if (!controller.signal.aborted) {
          setIsReferralValid(false);
          setReferralStatusMessage("Unable to verify referral code");
        }
      }
    }, 600);

    return () => {
      controller.abort();
      clearTimeout(delay);
    };
  }, [referralCode, user?.email]);

  const closeModal = () => {
    setSelectedJob(null);
    setCvFile(null);
    setReferralCode("");
  };

  const handleApply = () => {
    if (!selectedJob) {
      alert("Please select a job before applying.");
      return;
    }

    if (!user) {
      alert("User data is missing. Please log in again.");
      return;
    }

    if (userApplications.some((app) => app.job_code === selectedJob.id)) {
      alert("You have already applied for this job.");
      return;
    }

    if (hasReachedLimit) {
      alert("You have reached the maximum of 1 pending application.");
      return;
    }

    if (!cvFile) {
      alert("Please upload your CV before applying.");
      return;
    }

    // Show referral prompt
    setShowReferralPrompt(true);
  };

  const submitApplication = async () => {
    setIsSubmitting(true);

    try {
      // Upload CV
      const formData = new FormData();
      formData.append("resume", cvFile);
      const uploadRes = await fetch(`${SERVER_URL}/api/applications/upload`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!uploadRes.ok) throw new Error("Failed to upload CV");

      // Apply
      const payload = {
        job_code: selectedJob.id,
        position_title: selectedJob.title,
        department: selectedJob.department,
        email: user.email,
        firstname: user.firstname,
        middlename: user.middlename || "",
        lastname: user.lastname,
        resume: cvFile.name,
        referral_code: referralCode || "",
      };

      const res = await apiFetch(`${SERVER_URL}/api/applications/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok && data.message === "Application submitted successfully") {
        setShowSuccessModal(true);
        setUserApplications((prev) => [
          ...prev,
          { job_code: selectedJob.id, status: "Pending Review" },
        ]);
        setRefreshJobs((prev) => prev + 1); // Trigger re-fetch

        // Delay closing to allow UI to re-render job list with updated state
        setTimeout(() => {
          setSelectedJob(null);
          setCvFile(null);
          setReferralCode("");
        }, 200);
      } else {
        alert(data?.message || `Failed to submit application (${res.status})`);
      }
    } catch (err) {
      console.error("Submit error:", err);
      alert("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-linear-gradient p-6">
      <div className="bg-white p-4 sm:p-6 md:p-10 rounded-lg shadow-xl w-full max-w-xl flex flex-col text-center h-full">
        <img
          src={logo}
          alt="Callmax Logo"
          className="w-28 sm:w-32 md:w-36 lg:w-40 xl:w-44 mx-auto mb-6 max-w-full h-auto"
        />
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          Active Applications
        </h2>

        {/* 🔍 Search & Avatar Row */}
        <div className="flex justify-between items-center mb-4 relative">
          {/* Search Bar */}
          <div className="relative flex-1">
            <img
              src={searchIcon}
              alt="Search"
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 opacity-70"
            />
            <input
              type="text"
              placeholder="Search job titles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Avatar + Dropdown */}
          <div className="relative ml-4" ref={dropdownRef}>
            <div
              className="w-10 h-10 rounded-full overflow-hidden border-2 border-gray-300 cursor-pointer"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              {user?.picture &&
              !localStorage.getItem("applicationId")?.startsWith("manual_") ? (
                <img
                  src={user.picture}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <img
                  src="https://cdn-icons-png.flaticon.com/512/3177/3177440.png"
                  alt="Default User"
                  className="w-full h-full object-contain p-1"
                />
              )}
            </div>

            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded shadow-lg z-50 text-left text-sm">
                <div className="px-4 py-2 border-b text-gray-800 font-medium">
                  {`${user?.firstname || ""} ${user?.lastname || ""}`.trim() ||
                    "User"}
                </div>
                <ul>
                  <li
                    className="px-4 py-2 text-red-600 hover:bg-red-100 cursor-pointer"
                    onClick={handleLogout}
                  >
                    Logout
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <p className="text-gray-600">Loading...</p>
        ) : filteredJobs.length === 0 ? (
          <p className="text-gray-600">No job postings available.</p>
        ) : (
          <div className="overflow-y-auto flex-1 pr-1 max-h-[70vh]">
            <ul className="space-y-4">
              {filteredJobs
                .filter((job) => {
                  const userApp = userApplications.find(
                    (app) => app.job_code === job.id,
                  );
                  return userApp?.status !== "Failed";
                })
                .map((job) => {
                  const userApp = userApplications.find(
                    (app) => app.job_code === job.id,
                  );
                  const applicationStatus = userApp?.status;

                  return (
                    <li
                      key={job.id}
                      onClick={() => {
                        setSelectedJob(job);
                      }}
                      className={`bg-gray-100 shadow-md rounded-xl p-4 flex items-center justify-between cursor-pointer hover:bg-gray-200 hover:shadow-lg border border-gray-300 transition`}
                    >
                      <div className="text-left">
                        <p className="font-semibold text-gray-900">
                          ({job.id})
                        </p>
                        <p className="font-semibold text-gray-800">
                          {job.title}
                        </p>
                        <p className="text-sm text-gray-700">
                          {job.type} | {job.workSetup || "N/A"}
                        </p>
                      </div>
                      {applicationStatus && (
                        <div className="text-right text-sm text-[#162950] mb-10 font-medium">
                          {applicationStatus}
                        </div>
                      )}
                    </li>
                  );
                })}
            </ul>
          </div>
        )}
      </div>

      {/* MODAL */}

      {selectedJob && (
        <div className="fixed inset-0 z-50 flex justify-center items-center bg-black bg-opacity-50 p-4">
          <div className="bg-white w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl max-h-[90vh] rounded-2xl overflow-y-auto shadow-xl border border-gray-300">
            {/* HEADER */}
            <div className="bg-[#162950] rounded-2xl px-5 sm:px-6 py-5 text-white text-center relative">
              <div className="absolute top-2 left-1/2 transform -translate-x-1/2 h-1 w-10 bg-white rounded-full"></div>

              <p className="text-base sm:text-lg font-semibold mt-3 mb-2">
                {selectedJob?.id}
              </p>

              <h3 className="text-lg sm:text-xl font-bold">
                {selectedJob.title}
              </h3>

              <div className="mt-4 grid grid-cols-3 gap-2 text-xs sm:text-sm md:text-base text-center">
                <div>
                  <p className="font-medium">Date Posted</p>
                  <p>
                    {selectedJob.postedDate
                      ? `${getPostedAgo(selectedJob.postedDate)} ago`
                      : "N/A"}
                  </p>
                </div>

                <div>
                  <p className="font-medium">Department</p>
                  <p>{selectedJob.department}</p>
                </div>

                <div>
                  <p className="font-medium">Status</p>
                  <p>{selectedJob.status}</p>
                </div>
              </div>
            </div>

            {/* CONTENT (SINGLE COLUMN ALWAYS) */}
            <div className="p-4 sm:p-6 text-sm sm:text-base space-y-5 max-w-2xl mx-auto">
              {/* DESCRIPTION */}
              <div>
                <h4 className="font-semibold text-gray-800 text-base sm:text-lg">
                  Description
                </h4>

                <ul className="list-disc list-inside text-gray-700 mt-2 space-y-2">
                  {(selectedJob.description || "")
                    .split("\n")
                    .map((line, index) => (
                      <li key={index}>{line}</li>
                    ))}
                </ul>
              </div>

              {/* QUALIFICATIONS */}
              <div>
                <h4 className="font-semibold text-gray-800 text-base sm:text-lg">
                  Qualifications
                </h4>

                <ul className="list-disc list-inside text-gray-700 mt-2 space-y-2">
                  {selectedJob.requirements.split("\n").map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>

              {/* UPLOAD (STAYS BELOW ALWAYS) */}
              <div>
                <label className="block mt-2 mb-2 font-medium text-gray-700">
                  Upload CV <span className="text-red-500">*</span>
                </label>

                <input
                  type="file"
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm sm:text-base"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    setCvFile(file);
                  }}
                />
              </div>
            </div>

            {/* ACTIONS */}
            <div className="px-4 sm:px-6 pb-4 max-w-2xl mx-auto w-full">
              <button
                onClick={handleApply}
                disabled={
                  isSubmitting ||
                  userApplications.some(
                    (app) => app.job_code === selectedJob.id,
                  ) ||
                  (referralCode.trim() && isReferralValid === false)
                }
                className={`w-full py-3 rounded-lg font-semibold transition ${
                  isSubmitting ||
                  userApplications.some(
                    (app) => app.job_code === selectedJob.id,
                  ) ||
                  (referralCode.trim() && isReferralValid === false)
                    ? "bg-gray-400 cursor-not-allowed text-white"
                    : "bg-[#162950] hover:bg-[#1e3a78] text-white"
                }`}
              >
                {userApplications.some((app) => app.job_code === selectedJob.id)
                  ? "Already Applied"
                  : hasReachedLimit
                    ? "Limit Reached"
                    : "Apply for Job"}
              </button>

              <button
                onClick={closeModal}
                className="mt-4 w-full text-gray-500 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 1: Prompt Referral Choice */}
      {showReferralPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
          <div className="bg-white mx-4 px-6 py-5 sm:px-8 sm:py-6 rounded-md w-full max-w-md text-center">
            <h2 className="text-lg font-semibold text-[#162950] mb-2">
              Referral Code
            </h2>
            <p className="text-gray-700 mb-4">Do you have a referral code ?</p>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => {
                  setShowReferralPrompt(false);
                  setShowReferralInput(true);
                }}
                className="bg-gray-200 px-4 py-2 rounded hover:bg-gray-300"
              >
                Yes
              </button>
              <button
                onClick={() => {
                  setReferralCode("");
                  setShowReferralPrompt(false);
                  submitApplication(); // apply without referral
                }}
                className="bg-[#162950] text-white px-4 py-2 rounded hover:bg-[#1e3a78]"
              >
                None, Proceed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 2: Enter Referral Code */}
      {showReferralInput && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
          <div className="bg-white mx-4 px-6 py-5 sm:px-8 sm:py-6 rounded-md w-full max-w-md text-center">
            <h2 className="text-lg font-semibold text-[#162950] mb-2">
              Enter Referral Code
            </h2>

            {/* Input with Icon */}
            <div className="relative mb-3">
              <input
                type="text"
                value={referralCode}
                onChange={(e) => {
                  setReferralCode(e.target.value.trim());
                  setIsReferralValid(null);
                  setReferralStatusMessage("");
                }}
                className="border border-gray-300 rounded w-full px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 "
                placeholder="Enter employee number"
              />
              {isReferralValid !== null && (
                <div className="absolute top-1/2 right-3 transform -translate-y-1/2">
                  {isReferralValid ? (
                    <CheckCircleIcon className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircleIcon className="w-5 h-5 text-red-500" />
                  )}
                </div>
              )}
            </div>

            {/* Status message */}
            {referralStatusMessage && (
              <p
                className={`text-sm mb-2 ${
                  isReferralValid ? "text-green-600" : "text-red-600"
                }`}
              >
                {referralStatusMessage}
              </p>
            )}

            <div className="flex justify-center gap-4 mt-3">
              <button
                onClick={async () => {
                  if (!referralCode.trim()) {
                    setIsReferralValid(false);
                    setReferralStatusMessage("Please enter a referral code.");
                    return;
                  }
                  try {
                    const res = await apiFetch(
                      `${SERVER_URL}/api/validateReferral/${encodeURIComponent(
                        referralCode,
                      )}?email=${encodeURIComponent(user?.email || "")}`,
                    );
                    const data = await res.json();

                    if (data.valid) {
                      setIsReferralValid(true);
                      setReferralStatusMessage("Referral code is valid.");
                      setShowReferralInput(false);
                      submitApplication();
                    } else {
                      setIsReferralValid(false);
                      setReferralStatusMessage(
                        data.reason === "selfReferral"
                          ? "You can't use your own employee ID as a referral."
                          : "Referral code is invalid.",
                      );
                    }
                  } catch (err) {
                    setIsReferralValid(false);
                    setReferralStatusMessage("Unable to verify referral code.");
                  }
                }}
                className="bg-[#162950] text-white px-4 py-2 rounded hover:bg-[#1e3a78]"
              >
                Submit
              </button>

              <button
                onClick={() => {
                  setShowReferralInput(false);
                }}
                className="bg-gray-200 px-4 py-2 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {isSubmitting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="flex flex-col items-center space-y-3 bg-white p-6 rounded-lg shadow-lg">
            <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12 animate-spin border-t-blue-600"></div>
            <p className="text-gray-700 font-medium">
              Submitting your application...
            </p>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-60">
          <div className="bg-white p-6 mx-4 rounded-md shadow-md w-full max-w-sm text-center">
            <h2 className="text-lg font-semibold text-[#162950]">
              Application Submitted
            </h2>
            <p className="text-gray-700 mt-2">
              Thank you for applying! We’ve received your application.
            </p>
            <button
              onClick={() => {
                setShowSuccessModal(false);
                closeModal();
              }}
              className="mt-4 px-4 py-2 bg-[#162950] text-white rounded hover:bg-[#1e3a78] transition"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Applications;
